import { type Theme } from "@mui/material";

/**
 * Bottom padding to apply *inside* a page's scroll container so the last
 * scrollable content clears mobile bottom-anchored floating controls (e.g. an
 * add-action button or a floating action bar).
 *
 * Sizes the reservation to the shared `floatingNavigation` footprint —
 * `floatingNavigation.height + floatingNavigation.margin + spacing(3)` (the
 * extra `spacing(3)` is breathing room) — matching the inline formula used by
 * `LegacyRouteWrapper` and `SidePanelPageShell`.
 *
 * Returns `undefined` when the theme does not define `size.floatingNavigation`
 * (e.g. test themes / non-Berlin themes), matching those wrappers' guard so
 * callers don't accidentally pad an environment without the token.
 *
 * Apply this on the element that actually scrolls — never on the page wrapper
 * itself, which has `overflow: hidden` and would just shrink the visible page
 * area, leaving a static empty footer. Components that render on both mobile
 * and desktop must additionally scope this value to a mobile breakpoint (e.g.
 * `theme.breakpoints.down("md")`), since these floating controls are
 * mobile-only, so applying the padding unconditionally would leave dead space
 * at the bottom of desktop pages.
 */
export function getFloatingNavigationScrollPaddingBottom(theme: Theme): string | undefined {
  if (!theme.size?.floatingNavigation) {
    return undefined;
  }
  const { height, margin } = theme.size.floatingNavigation;
  return `calc(${height} + ${margin} + ${theme.spacing(3)} + var(--safe-area-inset-bottom))`;
}
